/**
 * SandboxBanner — a thin fixed bar pinned to the very top of every page,
 * shown ONLY on the sandbox host (sandbox.fresherbot.com). It marks the
 * environment as throwaway/test so no one mistakes it for production.
 *
 * When active it adds `.sandbox` to <html>; globals.css then shifts the
 * app content down (body padding + h-screen height) so nothing is hidden
 * behind the bar.
 *
 * Local testing: set VITE_SANDBOX_BANNER=1 to force it on.
 */

import { useEffect } from 'react';
import { FlaskConical } from 'lucide-react';

const SANDBOX_HOSTS = ['sandbox.fresherbot.com', 'dev.fresherbot.com'];
const isSandbox =
  typeof window !== 'undefined' &&
  (SANDBOX_HOSTS.includes(window.location.hostname) ||
    window.location.hostname.startsWith('sandbox.') ||
    window.location.hostname.startsWith('dev.') ||
    (import.meta as unknown as { env: Record<string, string> }).env?.VITE_SANDBOX_BANNER === '1');

export function SandboxBanner() {
  useEffect(() => {
    if (!isSandbox) return;
    document.documentElement.classList.add('sandbox');
    // Keep the sandbox out of search engines (belt-and-suspenders with the
    // nginx X-Robots-Tag header + robots.txt on the sandbox host).
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => {
      document.documentElement.classList.remove('sandbox');
      meta.remove();
    };
  }, []);

  if (!isSandbox) return null;

  return (
    <div
      className="fixed top-0 left-0 right-0 z-[99999] h-7 flex items-center justify-center gap-1.5 px-3 text-[12px] font-semibold tracking-tight"
      style={{
        background: 'linear-gradient(90deg, oklch(0.86 0.15 90) 0%, oklch(0.83 0.17 70) 100%)',
        color: 'oklch(0.34 0.09 60)',
        boxShadow: '0 1px 4px oklch(0.4 0.1 70 / 0.25)',
      }}
    >
      <FlaskConical size={13} className="flex-shrink-0" />
      <span className="truncate">
        Sandbox<span className="hidden sm:inline"> environment</span> — testing only<span className="hidden sm:inline">. Data may be reset at any time.</span>
      </span>
    </div>
  );
}
